// @ts-nocheck
import { Download, Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useOutletContext } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { pdfApi } from '@/services/api/pdf'
import { resolveLanguage } from '@/services/i18n/languages'
import { useCaseContext } from './CaseContext'
import type { CaseOutletContext } from './CaseLayout'

export default function ReportLayout() {
  const { caseId } = useCaseContext()
  const { t, i18n } = useTranslation()
  const parentCtx = useOutletContext<CaseOutletContext>()
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const handleExportPdf = useCallback(async () => {
    setIsGeneratingReport(true)
    try {
      toast.info(t('cases.report.export.toastGenerating'))
      const locale = resolveLanguage(i18n.language)
      const reportBlob = await pdfApi.generate({
        template: 'case_summary',
        subjectRef: caseId,
        caseId,
        locale,
      })
      const url = window.URL.createObjectURL(reportBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `case-${caseId}-report.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(t('cases.report.export.toastSuccess'))
    } catch {
      toast.error(t('cases.report.export.toastError'))
    } finally {
      setIsGeneratingReport(false)
    }
  }, [caseId, i18n.language, t])

  return (
    <div className="flex h-full flex-col bg-background">
      <Outlet context={{ ...parentCtx, handleExportPdf, isGeneratingReport }} />
    </div>
  )
}

export interface ReportOutletContext extends CaseOutletContext {
  handleExportPdf: () => Promise<void>
  isGeneratingReport: boolean
}

/**
 * Export-PDF action button for the report sub-pages. Intended for use in
 * PageShell's `toolbar` slot. The toolbar is rendered by TopBar (outside the
 * report Outlet tree), so the handler and pending flag must be passed as props
 * rather than pulled from outlet context at render time.
 */
export function ReportExportButton({
  onExport,
  isExporting,
}: {
  onExport: () => void | Promise<void>
  isExporting: boolean
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={onExport}
      disabled={isExporting}
      className="h-9 md:h-7"
    >
      {isExporting ? (
        <Loader2 className="size-3.5 mr-1.5 animate-spin" />
      ) : (
        <Download className="size-3.5 mr-1.5" />
      )}
      {isExporting ? t('cases.report.export.generating') : t('cases.report.export.exportPdf')}
    </Button>
  )
}
